import { formatDateTime } from '@crm/shared';
import { Badge, Button, Card, CardContent, cn, Select, Spinner } from '@crm/ui';
import { ArrowLeft } from 'lucide-react';
import {
  TICKET_STATUS_LABELS,
  type TicketStatus,
  useReplyTicket,
  useTicket,
  useUpdateTicket,
} from '../api';
import { MessageComposer } from './message-composer';

const TOM: Record<TicketStatus, string> = {
  OPEN: 'border-amber-500/50 text-amber-700 dark:text-amber-400',
  IN_PROGRESS: 'border-primary/50 text-primary',
  RESOLVED: 'border-emerald-500/50 text-emerald-700 dark:text-emerald-400',
};

/** A conversa de um chamado, do lado de quem abriu ou de quem atende. */
export function TicketThread({
  id,
  onBack,
  doSaas,
}: {
  id: string;
  onBack: () => void;
  doSaas?: boolean;
}) {
  const ticket = useTicket(id);
  const responder = useReplyTicket();
  const mudarStatus = useUpdateTicket();

  if (ticket.isLoading || !ticket.data) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  const dados = ticket.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">{dados.subject}</p>
          <p className="text-sm text-muted-foreground">
            {dados.companyName} - aberto em {formatDateTime(dados.createdAt)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {doSaas ? (
            <Select
              aria-label="Situacao do chamado"
              value={dados.status}
              onChange={(event) =>
                mudarStatus.mutate({ id, status: event.target.value as TicketStatus })
              }
            >
              {(Object.keys(TICKET_STATUS_LABELS) as TicketStatus[]).map((status) => (
                <option key={status} value={status}>
                  {TICKET_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          ) : (
            <Badge variant="outline" className={TOM[dados.status]}>
              {TICKET_STATUS_LABELS[dados.status]}
            </Badge>
          )}

          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            Chamados
          </Button>
        </div>
      </div>

      <Card variant="flush">
        <CardContent className="space-y-4 py-5">
          {dados.messages.map((mensagem) => (
            <div
              key={mensagem.id}
              className={cn(
                'max-w-[90%] space-y-2 rounded-card border px-3 py-2.5',
                mensagem.fromSaas
                  ? 'border-primary/30 bg-primary/5'
                  : 'ml-auto border-border bg-muted/40',
              )}
            >
              <p className="text-xs text-muted-foreground">
                {mensagem.authorName} - {formatDateTime(mensagem.createdAt)}
              </p>

              {mensagem.body ? (
                <p className="whitespace-pre-wrap text-sm">{mensagem.body}</p>
              ) : null}

              {mensagem.images.length > 0 ? (
                <ul className="flex flex-wrap gap-2">
                  {mensagem.images.map((image) => (
                    <li key={image}>
                      <a href={image} target="_blank" rel="noreferrer">
                        <img
                          src={image}
                          alt=""
                          className="max-h-48 rounded-card border border-border"
                        />
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card variant="flush">
        <CardContent className="py-5">
          <MessageComposer
            enviando={responder.isPending}
            onSend={(input) => responder.mutate({ id, ...input })}
          />
        </CardContent>
      </Card>
    </div>
  );
}
